/* Low-end device detection — the ONE definition. The UI (App.jsx: drops CSS
   transitions/animations), the lego routing gate (legoPlayerGate.js) and both
   players' tier configs all read this function, so a TV is either low-end
   everywhere or nowhere. (It used to be three rules that disagreed: a
   Chromium-version floor here, the per-OS regex in the players, and an extra
   webOS 3 check in App.jsx.)

   A device is low-end when ANY of these holds:

   1. OS + OS version — VITE_IS_LOW_END_DEVICE, e.g.
      {"webOS": "^(3|4)\\.", "TIZEN": "^(3|4)\\."}, matched against the
      platform's osName and the device's sdkVersion. This is the authoritative,
      ops-tunable rule; add an OS here when a whole release is too slow.
   2. Chromium < 60 from the user agent. On TVs the Chromium build is pinned by
      the OS release (webOS 3 = 38, webOS 4 = 53, Tizen 2017 = 47, Tizen 2018 =
      56), so this is the same OS-version signal — but it is SYNCHRONOUS: it
      works in the router gate and index.html before deviceInfo resolves, and on
      a degraded webOS WAM that answers deviceInfo with an empty object.
   3. Weak hardware — <= 2 cores or < 2 GB of RAM. Catches the budget boxes on
      NEWER OS releases (VIDAA, WhaleOS, Titan, recent Tizen) that no version
      rule can. `navigator.deviceMemory` exists from Chromium 63 and rounds
      down to a power of two (a 1.5 GB TV reports 1); platforms that report
      `cores`/`ramSize` in deviceInfo override the navigator values.

   Keep rules 2 and 3 in sync with the inline detection in index.html, which
   has to run before any bundle. */

/* External */
import platform from '.';

/* Utils */
import { IS_LOW_END_DEVICE, IS_LOW_END_DEVICE_LEGO } from '../env';

const CHROMIUM_FLOOR = 60;
const MIN_CORES = 2;
const MIN_RAM_GB = 2;

function getChromeVersion() {
  const match = /Chrome\/(\d+)/.exec(navigator.userAgent);
  return match ? Number.parseInt(match[1], 10) : undefined;
}

/* The last sdkVersion a caller passed in. App.jsx calls this with deviceInfo on
   every render, so callers that have no deviceInfo at hand (the router-time
   lego gate) still get the OS-version rule once the device has reported. */
let lastKnownSdkVersion;

function isLowEndOsVersion(osRules, osName, sdkVersion) {
  const rule = osName && sdkVersion ? osRules?.[osName] : undefined;
  return rule ? new RegExp(rule).test(sdkVersion) : false;
}

function detectLowEnd(osRules, deviceInfo = {}) {
  const {
    osName,
    cores = navigator.hardwareConcurrency,
    ramSize,
  } = deviceInfo;
  if (deviceInfo.sdkVersion) lastKnownSdkVersion = deviceInfo.sdkVersion;
  const sdkVersion = deviceInfo.sdkVersion || lastKnownSdkVersion;

  // webOS reports ramSize in GB; guard against MB-based platforms by treating
  // values over 64 as MB. Without a platform value, fall back to the browser's.
  let ramGb = navigator.deviceMemory;
  if (ramSize !== undefined) {
    ramGb = ramSize > 64 ? ramSize / 1024 : ramSize;
  }
  const chromeVersion = getChromeVersion();

  /* webOS deviceInfo does not carry osName — the platform module's is the
     reliable one. */
  return isLowEndOsVersion(osRules, osName || platform.osName, sdkVersion)
    || (chromeVersion !== undefined && chromeVersion < CHROMIUM_FLOOR)
    || (cores !== undefined && cores > 0 && cores <= MIN_CORES)
    || (ramGb !== undefined && ramGb > 0 && ramGb < MIN_RAM_GB);
}

/**
 * The app-wide rule (VITE_IS_LOW_END_DEVICE).
 *
 * @param {{ osName?: string, sdkVersion?: string, cores?: number, ramSize?: number }} [deviceInfo]
 * @returns {boolean}
 */
export function isLowEndDevice(deviceInfo = {}) {
  return detectLowEnd(IS_LOW_END_DEVICE, deviceInfo);
}

/**
 * The lego player's OWN rule (VITE_IS_LOW_END_DEVICE_LEGO): same detection as
 * isLowEndDevice (Chromium floor + weak hardware), but the OS + version rule
 * comes from its own env var, so the lego rollout can be tuned without moving
 * the app-wide low-end UI/NPAW behaviour. Used by the lego routing gate
 * (legoPlayerGate.js) and the lego player's tier selection.
 *
 * @param {{ osName?: string, sdkVersion?: string, cores?: number, ramSize?: number }} [deviceInfo]
 * @returns {boolean}
 */
export function isLowEndDeviceLego(deviceInfo = {}) {
  return detectLowEnd(IS_LOW_END_DEVICE_LEGO, deviceInfo);
}
